import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, CreditCard, MapPin, Printer, ShieldCheck, ShoppingCart, Store, Phone, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrderById, useUpdateOrderStatusMutation } from "@/hooks/queries";
import { mapOrderStatusToUi, mapUiStatusToOrderStatus, formatOrderDate } from "@/lib/mappers/order-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { OrderStatus, PaymentMethod, DeliveryType } from "@/types/enums";

const statusColor: Record<string, string> = {
  placed: "bg-info/10 text-info border-info/20",
  confirmed: "bg-warning/10 text-warning border-warning/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-success/10 text-success border-success/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const ORDER_STATUSES = ["placed", "confirmed", "dispatched", "delivered", "cancelled"] as const;

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4 py-2 border-b border-border/60 last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right sm:text-left break-all">{value}</span>
    </div>
  );
}

const AdminOrderDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const orderId = id ? Number(id) : 0;

  const { data: order, isLoading, refetch } = useOrderById(orderId);
  const updateStatusMutation = useUpdateOrderStatusMutation();

  const currentUiStatus = order ? mapOrderStatusToUi(order.status) : "placed";

  const handleUpdateStatus = async (val: string) => {
    const enumStatus = mapUiStatusToOrderStatus(val);
    if (enumStatus === undefined) return;
    try {
      await updateStatusMutation.mutateAsync({
        id: orderId,
        dto: { status: enumStatus },
      });
      toast({ title: "Order status updated", description: `Status changed to ${val}` });
      refetch();
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full rounded-2xl" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center space-y-4">
        <ShoppingCart className="w-12 h-12 text-muted-foreground" />
        <p className="text-lg font-semibold">Order not found</p>
        <Link to="/admin/orders">
          <Button variant="outline">Back to Orders</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Print styles block */}
      <style>{`
        @media print {
          html, body {
            background-color: #ffffff !important;
            color: #000000 !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
            font-size: 11pt !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Hide all screen layout wrappers */
          .no-print,
          aside,
          header,
          nav,
          button,
          [role="navigation"],
          .sidebar,
          .topbar {
            display: none !important;
          }
          #root, main, div {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          #print-invoice-section {
            display: block !important;
            width: 100% !important;
            padding: 24px !important;
            margin: 0 !important;
            background: white !important;
            color: black !important;
          }
          .invoice-border-b {
            border-bottom: 1px solid #e2e8f0 !important;
          }
          .invoice-border-t {
            border-top: 1px solid #e2e8f0 !important;
          }
          .invoice-border-double {
            border-bottom: 3px double #000000 !important;
          }
        }
      `}</style>

      {/* Screen-only view */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between no-print">
        <div className="space-y-1">
          <Link to="/admin/orders" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Orders
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Order #{order.orderNumber}</h1>
            <Badge variant="outline" className={statusColor[currentUiStatus]}>
              {currentUiStatus}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={handlePrint}>
            <Printer className="w-4 h-4" /> Print Invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 no-print">
        <div className="lg:col-span-2 space-y-6">
          {/* Order Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" /> Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <DetailRow label="Order Number" value={<span className="font-mono">{order.orderNumber}</span>} />
              <DetailRow label="Date & Time" value={new Date(order.createdAt).toLocaleString("en-IN")} />
              <DetailRow label="Store" value={order.storeName} />
              <DetailRow
                label="Delivery Type"
                value={order.deliveryType === DeliveryType.PlatformDelivery ? "Platform Delivery" : "Store Delivery"}
              />
              <DetailRow
                label="Payment Method"
                value={
                  order.paymentMethod === PaymentMethod.COD
                    ? "COD"
                    : order.paymentMethod === PaymentMethod.Wallet
                      ? "Wallet"
                      : "Razorpay"
                }
              />
              <DetailRow label="Payment Status" value={order.paymentStatusText} />
              {order.customerNotes && <DetailRow label="Customer Notes" value={order.customerNotes} />}
            </CardContent>
          </Card>

          {/* Items Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Items Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-border/60">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground">{item.productName}</p>
                      {item.variantInfo && (
                        <p className="text-xs text-muted-foreground mt-0.5">{item.variantInfo}</p>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-semibold">₹{item.totalPrice}</p>
                      <p className="text-xs text-muted-foreground">₹{item.unitPrice} × {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>₹{order.subTotal}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Delivery Charge</span>
                  <span>₹{order.deliveryCharge}</span>
                </div>
                {order.discountAmount > 0 && (
                  <div className="flex justify-between text-success font-medium">
                    <span>Discount</span>
                    <span>-₹{order.discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t border-border pt-2 text-primary">
                  <span>Total Amount</span>
                  <span>₹{order.totalAmount}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Quick Status Control Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Update Order Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current Status</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusColor[currentUiStatus]}>
                    {currentUiStatus}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Set New Status</span>
                <Select value={currentUiStatus} onValueChange={handleUpdateStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Customer details card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-primary" /> Customer Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Name</p>
                <p className="font-medium text-sm">{order.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Phone Number</p>
                <p className="font-medium text-sm">{order.customerPhone}</p>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Address Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1.5">
              <p className="font-bold">{order.shippingAddress.fullName}</p>
              <p className="text-muted-foreground">{order.shippingAddress.addressLine1}</p>
              {order.shippingAddress.addressLine2 && (
                <p className="text-muted-foreground">{order.shippingAddress.addressLine2}</p>
              )}
              {order.shippingAddress.landmark && (
                <p className="text-xs text-muted-foreground">Landmark: {order.shippingAddress.landmark}</p>
              )}
              <p className="text-muted-foreground">
                {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
              </p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground pt-1.5 border-t border-border mt-2">
                <Phone className="w-3.5 h-3.5" /> {order.shippingAddress.phone}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Printable Invoice section (Hidden by default, shown only during browser print) */}
      <div id="print-invoice-section" className="hidden p-8 bg-white text-black font-sans min-h-screen">
        {/* Brand Header */}
        <div className="flex justify-between items-start border-b pb-6 mb-6 invoice-border-b">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-black flex items-center">
              BIHARI<span className="text-primary font-bold ml-1" style={{ color: '#f97316' }}>MALL</span>
            </h1>
            <p className="text-xs text-muted-foreground mt-1">hyperlocal.com · Local Grocery & Quality Goods</p>
            <p className="text-[10px] text-muted-foreground">Bihar Hyperlocal Delivery Network Partner</p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-black uppercase tracking-wider text-black">TAX INVOICE</h2>
            <p className="text-sm mt-1">Invoice No: <span className="font-mono font-bold">{order.orderNumber}</span></p>
            <p className="text-xs text-muted-foreground">Order Date: {new Date(order.createdAt).toLocaleString("en-IN")}</p>
            <p className="text-xs text-muted-foreground">Print Date: {new Date().toLocaleDateString("en-IN")}</p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-8 mb-8 border-b pb-6 invoice-border-b">
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Sold By (Merchant Partner)</span>
            <p className="font-bold mt-1 text-base text-black">{order.storeName}</p>
            <p className="text-xs text-muted-foreground mt-1">Authorized Seller Partner</p>
            <p className="text-xs text-muted-foreground">Bihari Mall Hyperlocal Merchant Network</p>
            <p className="text-xs text-muted-foreground">Patna, Bihar, India</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Shipping & Billing Address</span>
            <p className="font-bold mt-1 text-base text-black">{order.shippingAddress.fullName}</p>
            <p className="text-xs text-muted-foreground mt-1">{order.shippingAddress.addressLine1}</p>
            {order.shippingAddress.addressLine2 && (
              <p className="text-xs text-muted-foreground">{order.shippingAddress.addressLine2}</p>
            )}
            {order.shippingAddress.landmark && (
              <p className="text-xs text-muted-foreground">Landmark: {order.shippingAddress.landmark}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
            </p>
            <p className="text-xs font-bold text-black mt-2">Phone: {order.shippingAddress.phone}</p>
          </div>
        </div>

        {/* Invoice Metadata Block */}
        <div className="grid grid-cols-2 gap-4 bg-muted/20 sm:grid-cols-4 p-3 rounded-lg border mb-8 text-xs">
          <div>
            <span className="block text-[10px] text-muted-foreground uppercase font-bold">Payment Method</span>
            <span className="font-semibold">{order.paymentMethodText}</span>
          </div>
          <div>
            <span className="block text-[10px] text-muted-foreground uppercase font-bold">Payment Status</span>
            <span className="font-semibold">{order.paymentStatusText}</span>
          </div>
          <div>
            <span className="block text-[10px] text-muted-foreground uppercase font-bold">Delivery Type</span>
            <span className="font-semibold">{order.deliveryTypeText}</span>
          </div>
          <div>
            <span className="block text-[10px] text-muted-foreground uppercase font-bold">Delivery Status</span>
            <span className="font-semibold">{order.statusText}</span>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-8">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-black font-bold uppercase text-[10px] text-muted-foreground">
                <th className="py-2 w-10 text-center">#</th>
                <th className="py-2 pl-4">Item Description</th>
                <th className="py-2 text-right w-24">Unit Price</th>
                <th className="py-2 text-center w-16">Qty</th>
                <th className="py-2 text-right w-24">Item Discount</th>
                <th className="py-2 text-right w-24">Net Amount</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, idx) => (
                <tr key={item.id} className="border-b invoice-border-b text-xs">
                  <td className="py-3 text-center text-muted-foreground font-mono">{idx + 1}</td>
                  <td className="py-3 pl-4">
                    <p className="font-semibold text-black">{item.productName}</p>
                    {item.variantInfo && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">{item.variantInfo}</p>
                    )}
                  </td>
                  <td className="py-3 text-right font-mono">₹{item.unitPrice.toFixed(2)}</td>
                  <td className="py-3 text-center font-mono">{item.quantity}</td>
                  <td className="py-3 text-right font-mono text-muted-foreground">₹{item.discountAmount.toFixed(2)}</td>
                  <td className="py-3 text-right font-semibold font-mono">₹{item.totalPrice.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pricing Summary */}
        <div className="flex justify-between items-start pt-4 border-t invoice-border-t">
          <div className="max-w-md text-xs text-muted-foreground">
            <h4 className="font-bold text-black uppercase tracking-wider mb-2">Terms & Declarations</h4>
            <p className="leading-relaxed">
              1. This is a computer-generated invoice. No physical signature is required.
            </p>
            <p className="leading-relaxed mt-1">
              2. Please preserve this copy for any refund, return, or exchange requests.
            </p>
            {order.customerNotes && (
              <div className="mt-3 bg-muted/40 p-2.5 rounded border">
                <span className="font-bold text-black block mb-0.5">Customer Delivery Notes:</span>
                <p className="italic text-foreground">{order.customerNotes}</p>
              </div>
            )}
          </div>
          
          <div className="w-80 space-y-2 text-xs border p-4 rounded-xl bg-muted/10">
            <div className="flex justify-between text-muted-foreground">
              <span>Item Subtotal:</span>
              <span className="font-mono">₹{order.subTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Delivery Charge:</span>
              <span className="font-mono">₹{order.deliveryCharge.toFixed(2)}</span>
            </div>
            {order.taxAmount > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Estimated GST (included):</span>
                <span className="font-mono">₹{order.taxAmount.toFixed(2)}</span>
              </div>
            )}
            {order.discountAmount > 0 && (
              <div className="flex justify-between text-success font-medium">
                <span>Coupon / Promotion Discount:</span>
                <span className="font-mono">-₹{order.discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t invoice-border-t pt-2 text-sm text-black invoice-border-double">
              <span>Total Payable Amount:</span>
              <span className="font-mono">₹{order.totalAmount.toFixed(2)}</span>
            </div>
            
            <div className="pt-2 text-[10px] text-center text-success font-bold bg-success/5 border border-success/20 rounded py-1 uppercase tracking-wide">
              Paid via {order.paymentMethodText}
            </div>
          </div>
        </div>

        {/* Footer Support info */}
        <div className="border-t border-dashed pt-8 text-center text-xs text-muted-foreground mt-16">
          <p className="font-semibold text-black">Thank you for ordering through Bihari Mall!</p>
          <p className="mt-1">For support, returns, or billing disputes, please contact us at support@biharimall.com</p>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetail;
